"use client";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { AddAddressInterface, LocationDetailsInterface } from "@/interface/account";
import { AppDispatch, AppState } from "@/store/configureStore";
import toast from "react-hot-toast";
import { yupResolver } from "@hookform/resolvers/yup";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { ADD_ADDRESS_API, DELETE_ADDRESS_API, DEFAULT_ADDRESS_API } from "@/constants";
import { listOfStates } from "@/utils/selectField";
import { updateAddress } from "@/store/slices/userSlice";

const useAddAddress = (onClose: () => void) => {
	const user = useSelector((state: AppState) => state.user);
	const dispatch = useDispatch<AppDispatch>();

	const addAddressSchema = yup.object({
		name: yup.string().required("Name is required"),
		address: yup.string().required("Address is required"),
		phone_number: yup.string().required("Phone number is required"),
		landmark: yup.string(),
	});

	const {
		register,
		watch,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm({
		resolver: yupResolver(addAddressSchema),
	});

	const handleAddAddress = async (
		data: AddAddressInterface,
		locationDetails: LocationDetailsInterface,
		setAddress: React.Dispatch<React.SetStateAction<string>>
	) => {
		try {
			const response = await axios.put(
				ADD_ADDRESS_API,
				{
					street: locationDetails.street,
					city: locationDetails.city,
					state: locationDetails.state,
					country: locationDetails.country || "Nigeria",
					name: data.name,
					landmark: data.landmark,
					phoneNumber: data.phone_number,
				},
				{
					headers: {
						Authorization: `Bearer ${user?.token}`,
					},
				}
			);

			dispatch(
				updateAddress({
					addresses: response?.data?.user?.addresses,
				})
			);
			onClose();
			setAddress("");
			reset();
			toast.remove();
			toast.success(`${response.data.msg}`);
		} catch (error: string | any) {
			console.log("API Error", error);
			toast.remove();
			toast.error(`An error occurred: ${error.response?.data?.message}`);
		}
	};

	const handleDeleteAddress = async (addressId: string) => {
		try {
			const response = await axios.delete(`${DELETE_ADDRESS_API}/${addressId}`, {
				headers: {
					Authorization: `Bearer ${user?.token}`,
				},
			});
			dispatch(
				updateAddress({
					...user,
					addresses: response?.data?.user?.addresses,
				})
			);
			onClose();
			toast.remove();
			toast.success(`${response.data.msg}`);
		} catch (error: string | any) {
			toast.remove();
			toast.error("Address not deleted");
			// console.log("API Error", error);
		}
	};

	const handleMakeDefault = async (addressId: string) => {
		try {
			const response = await axios.put(
				`${DEFAULT_ADDRESS_API}/${addressId}`,
				{},
				{
					headers: {
						Authorization: `Bearer ${user?.token}`,
					},
				}
			);

			dispatch(
				updateAddress({
					addresses: response?.data?.data.user?.addresses,
				})
			);
			toast.remove();
			toast.success(`${response.data.message}`);
		} catch (error: string | any) {
			toast.remove();
			toast.error(`${error.response.data.error}`);
			// console.log("API Error", error);
		}
	};

	return {
		register,
		handleSubmit,
		errors,
		watch,
		handleAddAddress,
		handleDeleteAddress,
		listOfStates,
		handleMakeDefault,
	};
};

export default useAddAddress;
